import { FC, HTMLAttributes } from 'react';
import styled, { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';

import { CommonPanel } from '../CommonPanel';
import { InfoButton } from '../InfoButton';
import { PlayersCards } from '../PlayersCards';
import { PrimaryPanelText } from '../PrimaryPanelText';
import { GamePart, GamePartProps } from '../GamePart';
import { GameType, GameTypeProps } from '../GameType';
import { BingoCardPattern, BingoCardPatternProps } from '../BingoCardPattern';
import { LiveIndicator } from '../LiveIndicator';
import { Ball30 } from '../Ball30';
import { Ball75 } from '../Ball75';
import { Ball80 } from '../Ball80';
import { Ball90 } from '../Ball90';
import fireBallGameDetails from '../../assets/apng/fireball-game-details.png';

const BALL_COMPONENTS = {
    30: Ball30,
    75: Ball75,
    80: Ball80,
    90: Ball90,
};

export type GameInfoProps = BingoCardPatternProps & {
    seconds: number;
    gameTitle: string;
    patternName: string;
    ticketPrice: string;
    gamePrize: string;
    gamePlayers: number | string;
    gameCards: number | string;
    hasJackpot: boolean;
    jackpotName?: string;
    jackpotCall?: number;
    jackpotPrize?: string;
    gameLinked?: boolean;
    gameContinued?: boolean;
    showTotalTickets: boolean;
    autoBuy: boolean;
    onInfoBox: () => void;
    onBuy: () => void;
    isGameLive: boolean;
};

export type CurrentGamePanelProps = HTMLAttributes<HTMLDivElement> &
    GameTypeProps &
    GamePartProps &
    GameInfoProps;

const CurrentGamePanelContainer = styled(CommonPanel)`
    height: auto;
    margin-bottom: 1rem;
`;

export const CurrentGamePart = styled(GamePart)<GameTypeProps>`
    margin-bottom: 0.625rem;

    ${({ gameType }) => `
        ${
            gameType !== 90
                ? `
                margin-right: 0.688rem;
            `
                : null
        }
    `};
`;

const UpperContainer = styled.div`
    display: flex;
    justify-content: space-between;
    width: 100%;
`;

const UpperTextContainer = styled.div<{ hasHeaderFireBall: boolean }>`
    display: flex;
    flex-direction: column;
    flex-basis: ${({ hasHeaderFireBall }) =>
        hasHeaderFireBall ? '53%' : '65%'};
`;

const UpperImageContainer = styled.div<
    GameTypeProps & { hasHeaderFireBall: boolean }
>`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    column-gap: 0.375rem;
    flex-basis: ${({ gameType, hasHeaderFireBall }) => {
        if (hasHeaderFireBall) {
            return gameType === 90 ? '47%' : '47%';
        }

        return gameType === 90 ? '26%' : '35%';
    }};
`;

const HeaderFireBall = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 3.125rem;
    height: 3.125rem;
    flex: 0 0 3.125rem;
`;

const HeaderFireBallImage = styled.img`
    position: absolute;
    top: 50%;
    left: 50%;
    width: calc(100% + 1.25rem);
    height: calc(100% + 1.25rem);
    transform: translate(-50%, -50%);
    pointer-events: none;
    z-index: 1;
`;

const PartPatternContainer = styled.div<GameTypeProps>`
    display: flex;
    justify-content: flex-end;

    ${({ gameType }) => `
        align-items: ${gameType === 90 ? 'flex-end' : 'center'};
        flex-basis: ${gameType === 90 ? '60%' : '68%'};
        flex-direction: ${gameType === 90 ? 'column' : 'row'};
    `};

    & > div:first-child {
        margin-right: 0.625rem;
    }
`;

const Divider = styled.hr`
    width: 91%;
    margin-left: 0;
    border-width: 0;
    height: 0.125rem;
    background-color: ${({ theme: { primaryPanel } }) =>
        primaryPanel.color.divider};
`;

const LowerContainer = styled.div`
    display: flex;
    width: 100%;
    margin-top: 0.5rem;
`;

const LowerTextContainer = styled.div<{ showTotalTickets: boolean }>`
    display: flex;
    justify-content: center;
    flex-direction: column;

    width: ${({ showTotalTickets }) => (showTotalTickets ? '55%' : '75%')};
`;

const LowerImageContainer = styled.div<{ showTotalTickets: boolean }>`
    display: flex;
    justify-content: space-between;
    align-items: center;

    width: ${({ showTotalTickets }) => (showTotalTickets ? '45%' : '25%')};
`;

const BingoCardPatternWrapper = styled(BingoCardPattern)`
    position: relative;
    top: 3px;
`;

export const GameContinuedIcon = styled.img`
    width: 2rem;
    height: 2rem;
`;

export const CurrentGamePanelMobile: FC<CurrentGamePanelProps> = ({
    gameTitle,
    patternName,
    gamePrize,
    gamePlayers,
    gameCards,
    hasJackpot,
    jackpotName,
    jackpotCall,
    jackpotPrize,
    currentPart,
    gameParts,
    fireBall,
    gameType,
    patternType,
    parts,
    gameLinked,
    gameContinued,
    showTotalTickets,
    onInfoBox,
    isGameLive,
    ...props
}) => {
    const { t } = useTranslation();
    const theme = useTheme();

    const { starImage4 } = theme.images;
    const CURRENT_GAME = t('infoBox.currentGame');
    const FireBall = BALL_COMPONENTS[gameType];
    const showFireBall = !!fireBall && fireBall > 0;

    return (
        <CurrentGamePanelContainer {...props} type="primary">
            <UpperContainer>
                <UpperTextContainer hasHeaderFireBall={showFireBall}>
                    <PrimaryPanelText
                        label={CURRENT_GAME}
                        value={gameTitle}
                        type="big"
                        gameLinked={gameLinked}
                    />
                    <Divider />
                </UpperTextContainer>
                <UpperImageContainer
                    gameType={gameType}
                    hasHeaderFireBall={showFireBall}
                >
                    <PartPatternContainer gameType={gameType}>
                        {isGameLive && <LiveIndicator />}
                        {gameContinued ? (
                            <GameContinuedIcon
                                src={starImage4}
                                alt="Continued Game Icon"
                            />
                        ) : (
                            <CurrentGamePart
                                gameParts={gameParts}
                                currentPart={currentPart}
                                fireBall={fireBall}
                                gameType={gameType}
                            />
                        )}
                        {showTotalTickets && (
                            <BingoCardPatternWrapper
                                patternType={patternType}
                                parts={parts}
                                currentPart={currentPart - 1}
                                gameType={gameType}
                            />
                        )}
                    </PartPatternContainer>
                    {showFireBall && FireBall && (
                        <HeaderFireBall>
                            <FireBall number={fireBall} size="xxsmall" />
                            <HeaderFireBallImage
                                src={fireBallGameDetails}
                                alt=""
                            />
                        </HeaderFireBall>
                    )}
                    <InfoButton handleClick={onInfoBox} />
                </UpperImageContainer>
            </UpperContainer>
            <LowerContainer>
                <LowerTextContainer showTotalTickets={showTotalTickets}>
                    <PrimaryPanelText
                        label={patternName}
                        value={gamePrize}
                        type="small"
                    />
                    {hasJackpot && (
                        <PrimaryPanelText
                            label={t('infoBox.jackpotFormatting', {
                                jpName: jackpotName,
                                jpCall: jackpotCall,
                            })}
                            value={jackpotPrize || ''}
                            type="small"
                        />
                    )}
                </LowerTextContainer>
                <LowerImageContainer showTotalTickets={showTotalTickets}>
                    <GameType gameType={gameType} />
                    {showTotalTickets ? (
                        <>
                            <PlayersCards type="players" text={gamePlayers} />
                            <PlayersCards type="cards" text={gameCards} />
                        </>
                    ) : (
                        <BingoCardPatternWrapper
                            patternType={patternType}
                            parts={parts}
                            currentPart={currentPart - 1}
                            gameType={gameType}
                        />
                    )}
                </LowerImageContainer>
            </LowerContainer>
        </CurrentGamePanelContainer>
    );
};
